import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Loader2, Heart, Shield } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PatientReportPage() {
  const { pharmacyCode } = useParams();
  const [pharmacyInfo, setPharmacyInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [form, setForm] = useState({
    patient_name: "",
    patient_phone: "",
    medications: [""],
    symptoms: [],
    severity: "moderee",
    onset_delay: "",
    description: "",
    age: "",
    is_pregnant: false,
  });

  useEffect(() => {
    axios.get(`${API}/pharmacovigilance/patient-form/${pharmacyCode}`)
      .then(res => { setPharmacyInfo(res.data); setLoading(false); })
      .catch(() => { setError("Pharmacie introuvable. Verifiez le QR code."); setLoading(false); });
  }, [pharmacyCode]);

  const addMed = () => setForm(f => ({ ...f, medications: [...f.medications, ""] }));
  const updateMed = (i, v) => setForm(f => ({ ...f, medications: f.medications.map((m, idx) => idx === i ? v : m) }));
  const removeMed = (i) => setForm(f => ({ ...f, medications: f.medications.filter((_, idx) => idx !== i) }));
  const toggleSymptom = (s) => setForm(f => ({ ...f, symptoms: f.symptoms.includes(s) ? f.symptoms.filter(x => x !== s) : [...f.symptoms, s] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_name || form.medications.filter(m => m).length === 0 || form.symptoms.length === 0) {
      setError("Veuillez remplir votre nom, au moins un medicament et un symptome.");
      return;
    }
    setSubmitting(true); setError(null);
    try {
      const res = await axios.post(`${API}/pharmacovigilance/patient-report/${pharmacyCode}`, {
        ...form,
        medications: form.medications.filter(m => m.trim()),
        age: form.age ? parseInt(form.age) : null,
      });
      setResult(res.data);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'envoi.");
    } finally { setSubmitting(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-emerald-50 to-white p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center" data-testid="report-success">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Merci pour votre signalement !</h1>
        <p className="text-slate-600 mb-4">Votre pharmacien <strong>{result?.pharmacy_name}</strong> a ete notifie et prendra contact avec vous si necessaire.</p>
        {result?.interactions_detectees > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mt-4">
            <AlertTriangle className="w-5 h-5 text-amber-600 mx-auto mb-1" />
            <p className="text-sm text-amber-800 font-semibold">{result.interactions_detectees} interaction(s) medicamenteuse(s) detectee(s)</p>
            <p className="text-xs text-amber-600 mt-1">Votre pharmacien vous contactera pour vous conseiller.</p>
          </div>
        )}
        <p className="text-xs text-slate-400 mt-6">CMES Pharma Intel - Pharmacovigilance</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white p-4 pb-20" data-testid="patient-report-page">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6 pt-4">
          <div className="w-14 h-14 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Shield className="w-7 h-7 text-sky-600" />
          </div>
          <h1 className="text-xl font-bold text-slate-900">Signalement Pharmacovigilance</h1>
          {pharmacyInfo && (
            <p className="text-sm text-sky-600 font-semibold mt-1">{pharmacyInfo.pharmacy_name}{pharmacyInfo.commune ? ` - ${pharmacyInfo.commune}` : ""}</p>
          )}
          <p className="text-xs text-slate-500 mt-2">Signalez un effet indesirable lie a un medicament. Votre pharmacien sera alerte.</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 text-sm text-red-700">{error}</div>
        )}

        <form onSubmit={submit} className="space-y-5">
          {/* Patient info */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <h2 className="text-sm font-bold text-slate-700 flex items-center gap-2"><Heart className="w-4 h-4 text-rose-500" /> Vos informations</h2>
            <input type="text" placeholder="Votre nom complet *" value={form.patient_name} onChange={e => setForm(f => ({...f, patient_name: e.target.value}))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" data-testid="patient-name-input" />
            <div className="grid grid-cols-2 gap-3">
              <input type="tel" placeholder="Telephone" value={form.patient_phone} onChange={e => setForm(f => ({...f, patient_phone: e.target.value}))}
                className="border border-slate-200 rounded-lg px-3 py-2.5 text-sm" data-testid="patient-phone-input" />
              <input type="number" placeholder="Age" value={form.age} onChange={e => setForm(f => ({...f, age: e.target.value}))}
                className="border border-slate-200 rounded-lg px-3 py-2.5 text-sm" data-testid="patient-age-input" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" checked={form.is_pregnant} onChange={e => setForm(f => ({...f, is_pregnant: e.target.checked}))} className="rounded" />
              Je suis enceinte
            </label>
          </div>

          {/* Medications */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <h2 className="text-sm font-bold text-slate-700">Medicament(s) concerne(s) *</h2>
            {form.medications.map((med, i) => (
              <div key={i} className="flex gap-2">
                <input type="text" placeholder={`Medicament ${i + 1}`} value={med} onChange={e => updateMed(i, e.target.value)}
                  className="flex-1 border border-slate-200 rounded-lg px-3 py-2.5 text-sm" data-testid={`med-input-${i}`} />
                {form.medications.length > 1 && (
                  <button type="button" onClick={() => removeMed(i)} className="text-red-400 hover:text-red-600 px-2">x</button>
                )}
              </div>
            ))}
            <button type="button" onClick={addMed} className="text-sky-600 text-xs font-semibold hover:underline">+ Ajouter un medicament</button>
          </div>

          {/* Symptoms */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <h2 className="text-sm font-bold text-slate-700">Symptomes ressentis *</h2>
            {pharmacyInfo?.symptom_categories?.map(cat => (
              <div key={cat.id}>
                <p className="text-xs font-semibold text-slate-500 mb-1.5">{cat.label}</p>
                <div className="flex flex-wrap gap-1.5">
                  {cat.symptoms.map(s => (
                    <button key={s} type="button" onClick={() => toggleSymptom(s)} data-testid={`symptom-${s.replace(/\s/g, '-')}`}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${form.symptoms.includes(s) ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {form.symptoms.length > 0 && (
              <p className="text-xs text-sky-600 font-semibold">{form.symptoms.length} symptome(s) selectionne(s)</p>
            )}
          </div>

          {/* Severity & Timing */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <h2 className="text-sm font-bold text-slate-700">Gravite et delai</h2>
            <select value={form.severity} onChange={e => setForm(f => ({...f, severity: e.target.value}))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" data-testid="severity-select">
              {pharmacyInfo?.severity_levels?.map(l => (
                <option key={l.id} value={l.id}>{l.label}</option>
              ))}
            </select>
            <select value={form.onset_delay} onChange={e => setForm(f => ({...f, onset_delay: e.target.value}))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" data-testid="delay-select">
              <option value="">Delai d'apparition...</option>
              <option value="< 1h">Moins d'1 heure apres la prise</option>
              <option value="1-24h">1 a 24 heures</option>
              <option value="1-7j">1 a 7 jours</option>
              <option value="> 7j">Plus d'une semaine</option>
            </select>
          </div>

          {/* Description */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <h2 className="text-sm font-bold text-slate-700 mb-2">Description (optionnel)</h2>
            <textarea placeholder="Decrivez ce que vous avez ressenti..." value={form.description}
              onChange={e => setForm(f => ({...f, description: e.target.value}))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm h-20 resize-none" data-testid="description-textarea" />
          </div>

          {/* Submit */}
          <Button type="submit" disabled={submitting} className="w-full py-6 text-base font-bold bg-sky-600 hover:bg-sky-700 text-white rounded-xl shadow-lg" data-testid="submit-report-btn">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Envoyer mon signalement"}
          </Button>
          <p className="text-center text-[10px] text-slate-400">Vos donnees sont confidentielles et transmises uniquement a votre pharmacien.</p>
        </form>
      </div>
    </div>
  );
}
